/**
 * Shared shape + helpers for the remote scene-background manifest
 * (`server-data/backgrounds/backgrounds.json`, and its mobile sibling
 * `server-data/backgrounds/mobile/backgrounds.json`).
 *
 * Two shapes are accepted for each collection (`images` / `videos`):
 *
 * 1. An explicit array — the original format, one object per asset.
 * 2. A compact "sequence spec" — assets are numbered sequentially
 *    (`0001.jpg`, `0002.jpg`, …), so we only describe the directory, the
 *    extension and how many there are. Adding a background becomes a
 *    one-character change (`count: 9` → `count: 10`) instead of a new
 *    hand-written block.
 *
 * `normalizeBackgroundsConfig` collapses both into the explicit array form so
 * consumers never need to care which one was served.
 */

export interface BackgroundResource {
  src?: string
  previewUrl?: string
}

export interface BackgroundEntry {
  id: string
  name: string
  type: string
  price: number
  resources: BackgroundResource
}

export interface BackgroundTab {
  id: string
  name: string
  field: string
  isDefault?: boolean
}

/** Compact description of a numbered run of files, e.g. `0001.jpg` … `0009.jpg`. */
export interface BackgroundSequenceSpec {
  /** Directory holding the files, relative to the backgrounds root. e.g. `desktop/static`. */
  dir: string
  /** File extension without the leading dot. Defaults to `jpg` / `mp4` by collection. */
  ext?: string
  /** How many files the run contains. */
  count: number
  /** First index of the run. Defaults to `1`. */
  start?: number
  /** Zero-padding width of the index. Defaults to `4` (`0001`). */
  digits?: number
  /**
   * File name template. Supports `{index}` (zero-padded), `{n}` (unpadded) and
   * `{ext}`. Defaults to `{index}.{ext}`.
   */
  pattern?: string
  /** Price applied to every generated entry. Defaults to `0`. */
  price?: number
  /** Directory holding the still thumbnails (videos only). Omit to skip previews. */
  previewDir?: string
  /** Thumbnail extension without the leading dot. Defaults to `jpg`. */
  previewExt?: string
  /** Thumbnail file name template. Defaults to `{index}.{previewExt}`. */
  previewPattern?: string
  /** Prefix for generated ids. Defaults to `img` / `vid` by collection. */
  idPrefix?: string
}

export type BackgroundCollection = BackgroundEntry[] | BackgroundSequenceSpec

export interface BackgroundsConfig {
  tabs: BackgroundTab[]
  images: BackgroundCollection
  videos: BackgroundCollection
}

export interface NormalizedBackgroundsConfig {
  tabs: BackgroundTab[]
  images: BackgroundEntry[]
  videos: BackgroundEntry[]
}

export type BackgroundVariant = 'desktop' | 'mobile'

const DEFAULT_EXT: Record<string, string> = { images: 'jpg', videos: 'mp4' }
const DEFAULT_ID_PREFIX: Record<string, string> = { images: 'img', videos: 'vid' }

function trimSlashes(value: string): string {
  return value.replace(/^\/+/, '').replace(/\/+$/, '')
}

function joinPath(dir: string, file: string): string {
  return dir ? `${dir}/${file}` : file
}

function renderPattern(pattern: string, vars: { index: string, n: string, ext: string }): string {
  return pattern
    .replace(/\{index\}/g, vars.index)
    .replace(/\{n\}/g, vars.n)
    .replace(/\{ext\}/g, vars.ext)
}

function isSequenceSpec(value: unknown): value is BackgroundSequenceSpec {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function normalizeExt(ext: string | undefined): string {
  return (ext ?? '').replace(/^\./, '')
}

/** Expands a compact sequence spec into explicit entries. */
export function expandBackgroundSequence(
  spec: BackgroundSequenceSpec,
  field: 'images' | 'videos',
): BackgroundEntry[] {
  const count = Number.isFinite(spec.count) ? Math.max(0, Math.floor(spec.count)) : 0
  if (count <= 0)
    return []

  const start = spec.start ?? 1
  const digits = spec.digits ?? 4
  const ext = normalizeExt(spec.ext) || DEFAULT_EXT[field]
  const idPrefix = spec.idPrefix ?? DEFAULT_ID_PREFIX[field]
  const price = spec.price ?? 0
  const dir = trimSlashes(spec.dir ?? '')
  const pattern = spec.pattern ?? `{index}.${ext}`
  const previewDir = spec.previewDir ? trimSlashes(spec.previewDir) : undefined
  const previewExt = normalizeExt(spec.previewExt) || 'jpg'
  const previewPattern = spec.previewPattern ?? `{index}.${previewExt}`

  const entries: BackgroundEntry[] = []
  for (let offset = 0; offset < count; offset++) {
    const n = start + offset
    const index = String(n).padStart(digits, '0')

    const resources: BackgroundResource = {
      src: joinPath(dir, renderPattern(pattern, { index, n: String(n), ext })),
    }
    if (previewDir) {
      resources.previewUrl = joinPath(
        previewDir,
        renderPattern(previewPattern, { index, n: String(n), ext: previewExt }),
      )
    }

    entries.push({
      id: `${idPrefix}_${index}`,
      name: '',
      type: ext,
      price,
      resources,
    })
  }

  return entries
}

function normalizeCollection(collection: unknown, field: 'images' | 'videos'): BackgroundEntry[] {
  if (Array.isArray(collection))
    return collection as BackgroundEntry[]
  if (isSequenceSpec(collection))
    return expandBackgroundSequence(collection as BackgroundSequenceSpec, field)
  return []
}

/**
 * Accepts the raw parsed JSON (either shape) and returns it with both
 * collections resolved to explicit entry arrays.
 */
export function normalizeBackgroundsConfig(raw: unknown): NormalizedBackgroundsConfig {
  const config = (raw ?? {}) as Partial<BackgroundsConfig>

  return {
    tabs: Array.isArray(config.tabs) ? config.tabs : [],
    images: normalizeCollection(config.images, 'images'),
    videos: normalizeCollection(config.videos, 'videos'),
  }
}

/** Path of the manifest for a variant, relative to the backgrounds root. */
export function backgroundManifestPath(variant: BackgroundVariant): string {
  return variant === 'mobile' ? 'mobile/backgrounds.json' : 'backgrounds.json'
}

/**
 * Manifests to try, in order. The mobile set is optional — until it is
 * uploaded the phone build keeps serving the desktop artwork instead of an
 * empty gallery, so mobile is always followed by a desktop fallback.
 */
export function backgroundManifestCandidates(variant: BackgroundVariant): BackgroundVariant[] {
  return variant === 'mobile' ? ['mobile', 'desktop'] : ['desktop']
}
